import React, { useState, useEffect } from 'react';
import { searchAPI } from '../utils/api';
import { Input } from './ui/input';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Search, X, ArrowLeft, MessageSquare } from 'lucide-react';

const MessageSearch = ({ open, onClose, onSelectMessage }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (!open) { setQuery(''); setResults([]); setSearched(false); }
  }, [open]);

  useEffect(() => {
    if (query.length > 0) {
      const timer = setTimeout(() => handleSearch(), 500);
      return () => clearTimeout(timer);
    } else { setResults([]); setSearched(false); }
  }, [query]);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      const response = await searchAPI.searchMessages(query);
      setResults(response.data.results || []);
    } catch { setResults([]); }
    finally { setLoading(false); }
  };

  const handleSelectResult = (result) => {
    onSelectMessage(result.channel, result.message);
    onClose();
    setQuery('');
    setResults([]);
  };

  const highlightText = (text, q) => {
    if (!text || !q) return text;
    const parts = text.split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === q.toLowerCase()
        ? <mark key={i} className="bg-[#A4F4B0] text-[#111B21] font-medium rounded-sm px-0.5">{part}</mark>
        : part
    );
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-white dark:bg-[#0B141A] flex flex-col" data-testid="search-dialog">
      {/* Search Header */}
      <div className="glass-header px-3 py-2.5 flex items-center gap-2 shrink-0">
        <button onClick={onClose} className="p-1.5 -ml-1 rounded-full hover:bg-[#F0F2F5] transition-colors" data-testid="search-back-btn">
          <ArrowLeft className="w-5 h-5 text-[#111B21]" />
        </button>
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8696A0]" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search messages..."
            className="pl-9 pr-9 h-9 rounded-full bg-[#F0F2F5] border-0 text-[14px] focus-visible:ring-0 focus-visible:ring-offset-0"
            autoFocus
            data-testid="search-input"
          />
          {query && (
            <button
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5"
              onClick={() => setQuery('')}
            >
              <X className="w-4 h-4 text-[#8696A0]" />
            </button>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto"  data-testid="search-results-container">
        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#00A884] border-t-transparent" />
          </div>
        )}

        {!loading && searched && results.length === 0 && (
          <div className="text-center py-16 px-8">
            <Search className="w-10 h-10 mx-auto mb-3 text-[#8696A0] opacity-40" />
            <p className="text-[15px] text-[#667781]">No results found</p>
            <p className="text-[13px] text-[#8696A0] mt-1">Try different keywords</p>
          </div>
        )}

        {!loading && results.length > 0 && (
          <div data-testid="search-results">
            <p className="px-5 py-2 text-[12px] text-[#8696A0] font-medium">
              {results.length} result{results.length !== 1 && 's'}
            </p>
            {results.map((result) => (
              <div
                key={result.message?.id || result.id}
                onClick={() => handleSelectResult(result)}
                className="flex items-start gap-3 px-4 py-3 hover:bg-[#F0F2F5] active:bg-[#E9EDEF] cursor-pointer transition-colors border-b border-[#E9EDEF]/50"
                data-testid={`search-result-${result.message?.id || result.id}`}
              >
                <Avatar className="w-10 h-10 shrink-0 mt-0.5">
                  <AvatarImage src={result.sender?.avatar_url} />
                  <AvatarFallback className="bg-[#4338CA] text-white text-sm">
                    {result.sender?.name?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <span className="text-[15px] font-medium text-[#111B21] truncate">
                      {result.sender?.name}
                    </span>
                    <span className="text-[11px] text-[#8696A0] shrink-0">
                      {result.message?.created_at && new Date(result.message.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-[13px] text-[#667781] mb-0.5 truncate">
                    {result.channel?.name || 'Direct Message'}
                  </p>
                  <p className="text-[14px] text-[#111B21] line-clamp-2 leading-snug">
                    {highlightText(result.message?.content, query)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {!searched && !loading && (
          <div className="text-center py-16 px-8">
            <MessageSquare className="w-10 h-10 mx-auto mb-3 text-[#8696A0] opacity-30" />
            <p className="text-[15px] text-[#667781]">Search across all chats</p>
            <p className="text-[13px] text-[#8696A0] mt-1">Find messages, photos, links and more</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageSearch;
